import { useEffect, useMemo, useState } from 'react'
import {
  GanttChartSquare,
  Calendar,
  Link2,
  AlertCircle,
  ArrowUp,
  Minus,
  ArrowDown,
} from 'lucide-react'
import { useProjectStore, type Task, type TaskPriority } from '../../store/projectStore'
import { usePermissions } from '../../hooks/usePermissions'
import TaskDetailModal from './TaskDetailModal'

const PRIORITY_BADGE: Record<TaskPriority, { icon: React.ElementType; className: string }> = {
  urgent: { icon: AlertCircle, className: 'text-red-700 bg-red-100' },
  high:   { icon: ArrowUp,     className: 'text-orange-700 bg-orange-100' },
  medium: { icon: Minus,       className: 'text-gray-700 bg-gray-100' },
  low:    { icon: ArrowDown,   className: 'text-blue-700 bg-blue-100' },
}

const STATUS_DOT: Record<string, string> = {
  todo: 'bg-gray-400',
  in_progress: 'bg-blue-500',
  review: 'bg-purple-500',
  done: 'bg-green-500',
  blocked: 'bg-red-500',
}

function parseDate(v?: string | null) {
  if (!v) return null
  const d = new Date(v + 'T00:00:00')
  return isNaN(d.getTime()) ? null : d
}

function startOfWeek(d: Date) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  const day = x.getDay() // 0 (Sun) - 6 (Sat)
  const offsetToMonday = day === 0 ? -6 : 1 - day
  x.setDate(x.getDate() + offsetToMonday)
  return x
}

function endOfWeek(d: Date) {
  const s = startOfWeek(d)
  const e = new Date(s)
  e.setDate(e.getDate() + 6)
  e.setHours(23, 59, 59, 999)
  return e
}

function fmtRange(start: Date, end: Date) {
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
  const sm = start.toLocaleDateString(undefined, { month: 'short' })
  const em = end.toLocaleDateString(undefined, { month: 'short' })
  if (sameMonth) return `${sm} ${start.getDate()}–${end.getDate()}`
  return `${sm} ${start.getDate()} – ${em} ${end.getDate()}`
}

interface GanttTimelineListProps {
  projectId: number
}

interface BucketedTask {
  task: Task
  start: Date
  end: Date
  // pixel offset & width as a fraction of the week (0..100)
  offsetPct: number
  widthPct: number
  spansBefore: boolean
  spansAfter: boolean
}

interface WeekBucket {
  weekStart: Date
  weekEnd: Date
  label: string
  rangeLabel: string
  tasks: BucketedTask[]
}

export default function GanttTimelineList({ projectId }: GanttTimelineListProps) {
  const { gantt, fetchGantt } = useProjectStore()
  const { can } = usePermissions()
  const [openTask, setOpenTask] = useState<Task | null>(null)

  const canEdit = can('admin_project_monitoring', 'edit') || can('pwa_project_monitoring', 'edit')
  const canDelete = can('admin_project_monitoring', 'delete') || can('pwa_project_monitoring', 'delete')

  useEffect(() => {
    fetchGantt(projectId)
  }, [projectId, fetchGantt])

  const buckets: WeekBucket[] = useMemo(() => {
    if (!gantt) return []
    const tasks = gantt.tasks.filter((t) => t.start_date || t.due_date)
    if (tasks.length === 0) return []

    // Resolve a start/end Date per task
    const resolved = tasks.map((t) => {
      const s = parseDate(t.start_date) || parseDate(t.due_date)!
      const e = parseDate(t.due_date) || parseDate(t.start_date)!
      const safeEnd = e.getTime() >= s.getTime() ? e : s
      return { task: t, start: s, end: safeEnd }
    })

    // Earliest week start, latest week end
    const earliest = resolved.reduce((acc, r) => (r.start < acc ? r.start : acc), resolved[0].start)
    const latest = resolved.reduce((acc, r) => (r.end > acc ? r.end : acc), resolved[0].end)

    const buckets: WeekBucket[] = []
    let cursor = startOfWeek(earliest)
    const last = endOfWeek(latest)

    while (cursor <= last) {
      const ws = new Date(cursor)
      const we = endOfWeek(ws)

      const matched: BucketedTask[] = resolved
        .filter((r) => r.start <= we && r.end >= ws)
        .map((r) => {
          const weekMs = we.getTime() - ws.getTime()
          const clampedStart = r.start < ws ? ws : r.start
          const clampedEnd = r.end > we ? we : r.end
          const offsetPct = ((clampedStart.getTime() - ws.getTime()) / weekMs) * 100
          const widthPct = Math.max(4, ((clampedEnd.getTime() - clampedStart.getTime()) / weekMs) * 100)
          return {
            task: r.task,
            start: r.start,
            end: r.end,
            offsetPct,
            widthPct,
            spansBefore: r.start < ws,
            spansAfter: r.end > we,
          }
        })
        // Sort by offset then by title
        .sort((a, b) => a.offsetPct - b.offsetPct || a.task.title.localeCompare(b.task.title))

      buckets.push({
        weekStart: ws,
        weekEnd: we,
        label: `Week of ${ws.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
        rangeLabel: fmtRange(ws, we),
        tasks: matched,
      })

      cursor = new Date(ws)
      cursor.setDate(cursor.getDate() + 7)
    }

    return buckets.filter((b) => b.tasks.length > 0)
  }, [gantt])

  const depMap = useMemo(() => {
    const m = new Map<number, number[]>() // task_id -> deps on those task ids
    if (!gantt) return m
    for (const d of gantt.dependencies) {
      const list = m.get(d.task_id) || []
      list.push(d.depends_on_task_id)
      m.set(d.task_id, list)
    }
    return m
  }, [gantt])

  const taskTitleMap = useMemo(() => {
    const m = new Map<number, string>()
    if (gantt) for (const t of gantt.tasks) m.set(t.id, t.title)
    return m
  }, [gantt])

  if (!gantt) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center mx-3">
        <GanttChartSquare className="mx-auto h-10 w-10 text-gray-400 animate-pulse" />
        <p className="mt-3 text-sm text-gray-500">Loading timeline…</p>
      </div>
    )
  }

  if (buckets.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center mx-3">
        <GanttChartSquare className="mx-auto h-10 w-10 text-gray-400" />
        <h3 className="mt-3 text-sm font-medium text-gray-900">No scheduled tasks</h3>
        <p className="mt-1 text-sm text-gray-500">Tasks need a start or due date to show on the timeline.</p>
      </div>
    )
  }

  return (
    <div className="px-3 pb-32 space-y-5">
      {buckets.map((b, idx) => (
        <section key={idx}>
          <div className="sticky top-0 z-10 -mx-3 px-3 py-2 bg-gray-50 border-y border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">{b.label}</h3>
            <p className="text-[11px] text-gray-500">{b.rangeLabel}</p>
          </div>

          <ul className="mt-3 space-y-2.5">
            {b.tasks.map(({ task, offsetPct, widthPct, spansBefore, spansAfter, start, end }) => {
              const prio = PRIORITY_BADGE[task.priority]
              const PrioIcon = prio.icon
              const deps = depMap.get(task.id) || []
              const isDone = task.status === 'done'
              const isBlocked = task.status === 'blocked'

              return (
                <li
                  key={task.id}
                  onClick={() => setOpenTask(task)}
                  className="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm active:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`h-2 w-2 rounded-full shrink-0 ${STATUS_DOT[task.status] || 'bg-gray-400'}`} />
                      <h4 className="text-sm font-semibold text-gray-900 truncate">{task.title}</h4>
                    </div>
                    <span className={`inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-medium ${prio.className}`}>
                      <PrioIcon className="h-2.5 w-2.5" />
                    </span>
                  </div>

                  {/* Mini bar showing position within the week */}
                  <div className="relative mt-2.5 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={`absolute top-0 h-full ${
                        isDone ? 'bg-green-500' : isBlocked ? 'bg-red-500' : 'bg-primary-500'
                      } ${spansBefore ? 'rounded-l-none' : 'rounded-l-full'} ${
                        spansAfter ? 'rounded-r-none' : 'rounded-r-full'
                      }`}
                      style={{ left: `${offsetPct}%`, width: `${widthPct}%` }}
                    />
                  </div>

                  <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      {start.getTime() !== end.getTime() && (
                        <>
                          {' → '}
                          {end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </>
                      )}
                    </span>
                    {task.completion_percent > 0 && (
                      <span>{task.completion_percent}%</span>
                    )}
                    {task.assignee_name && (
                      <span className="truncate">· {task.assignee_name}</span>
                    )}
                  </div>

                  {deps.length > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
                      <Link2 className="h-3 w-3" />
                      blocked by {deps.map((id) => taskTitleMap.get(id) || `#${id}`).join(', ')}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      ))}

      <TaskDetailModal
        isOpen={openTask !== null}
        task={openTask}
        projectId={projectId}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={() => { setOpenTask(null); fetchGantt(projectId) }}
      />
    </div>
  )
}
